import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { graphql } from 'gatsby';
import { RichTextBlock } from 'prismic-reactjs';
import { GatsbyImage, GatsbyImageProps } from 'gatsby-plugin-image';

import { useDualBlockTextBackgroundPrimaryContentAnimation } from './hooks';

import { RichText } from 'components/Prismic';

interface IPrimaryContent {
  title: RichTextBlock[];
  description: RichTextBlock[];
  image: Pick<GatsbyImageProps, 'image' | 'alt'> | null;
}

const Container = styled.div`
  box-sizing: border-box;
  display: grid;
  position: relative;
  row-gap: 7.25rem;
  padding: 36.875rem 0;
`;

const ImageContainer = styled.div`
  position: absolute;
  width: 100%;
  height: 130%;
`;

const BackgroundImage = styled(GatsbyImage)`
  width: 100%;
  height: 100%;
`;

const Content = styled.div`
  position: relative;
  z-index: 1;
`;

const AnimationWrapper = styled.div`
  display: grid;
  position: absolute;
  top: 0;
  left: 0;
  justify-content: center;
  justify-items: center;
  width: 100%;
`;

const Title = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;

    & > h2 {
      text-align: center;
      line-height: 0.965;
      letter-spacing: -0.04em;
      color: ${colors.black};
      font-size: 9rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};

      strong {
        ${tp.useFont(fonts => fonts.Switzer.lightBold)};
      }
    }
  `}
`;

const Description = styled(RichText)`
  ${({ theme: { colors, tp } }) => css`
    display: flex;
    background-color: ${colors.white}2E;
    backdrop-filter: blur(2.6875rem);

    & > p {
      padding: 7rem 4.5rem;
      text-align: left;
      line-height: 1.2;
      color: ${colors.black};
      font-size: 2.75rem;

      ${tp.useFont(fonts => fonts.Switzer.light)};
    }
  `}
`;

const PrimaryContent: React.FC<IPrimaryContent> = ({ title, description, image }) => {
  const { containerRef, backgroundRef, contentRef, wrapperRef } =
    useDualBlockTextBackgroundPrimaryContentAnimation();

  return (
    <Container ref={containerRef}>
      {image ? (
        <ImageContainer ref={backgroundRef}>
          <BackgroundImage {...image} />
        </ImageContainer>
      ) : null}
      <Content ref={contentRef}>
        <AnimationWrapper ref={wrapperRef}>
          <Title render={title} />
          <Description render={description} />
        </AnimationWrapper>
      </Content>
    </Container>
  );
};

export const desktopDualBlockTextBackgroundPrimaryContentFragment = graphql`
  fragment SolutionDesktopDualBlockTextBackgroundPrimaryContentData on PrismicSolutionDataDesktopSolutionDualBlockTextBackground {
    primary {
      solution_dual_block_text_background_primary_image {
        gatsbyImageData
        alt
      }
      solution_dual_block_text_background_primary_title {
        richText
      }
      solution_dual_block_text_background_primary_description {
        richText
      }
    }
  }
`;

export default PrimaryContent;
